import React, { useEffect, useRef, useState } from "react";
import {
  EmptyPortfolioIllustration,
  CheckIcon,
  DownPolygon,
  UpPolygon,
  ChevronDownIcon,
} from "./icon";
import { EmptyDataStateIcon } from "../../portfolio-ai/components/icons/EmptyDataStateIcon";

const BASE_URL = import.meta.env.VITE_SIMPLIZE_BASE_URL || "";

const GRID = "grid grid-cols-[1fr_102px_102px_88px]";
const GAIN = "#2aa97a";
const LOSS = "#d95656";

function formatPrice(price) {
  if (price == null) return "-";
  return price.toLocaleString("vi-VN");
}

function formatChange(changePct) {
  if (changePct == null) return { text: "-", color: "#111827" };
  const sign = changePct >= 0 ? "+" : "";
  return {
    text: `${sign}${changePct.toFixed(2)}%`,
    color: changePct >= 0 ? GAIN : LOSS,
  };
}

function formatPricePct(changePct) {
  if (changePct == null) return { text: "-", color: "#111827" };
  const sign = changePct >= 0 ? "+" : "";
  return {
    icon: changePct >= 0 ? <UpPolygon /> : <DownPolygon />,
    text: `${sign}${changePct.toFixed(2)}%`,
    color: changePct >= 0 ? GAIN : LOSS,
  };
}

function reportCountLabel(count) {
  return count > 0 ? String(count) : "-";
}

function SimplePriceCell({ item }) {
  return (
    <span className="text-[14px] font-medium text-[#111827] leading-[20px] text-right truncate">
      {formatPrice(item.price)}
    </span>
  );
}

function PortfolioPriceCell({ item }) {
  const changePrice = formatPricePct(item.change_pct);
  return (
    <div className="flex flex-col text-right">
      <p className="text-[14px] font-medium text-[#111827] leading-[20px] truncate">
        {formatPrice(item.price)}
      </p>
      <p
        className="text-[12px] font-medium leading-[14px] text-right truncate flex items-center justify-end"
        style={{ color: changePrice.color }}
      >
        {changePrice.icon}
        {changePrice.text}
      </p>
    </div>
  );
}

const SECTION_CONFIGS = {
  portfolio: {
    menuHeading: "Danh mục đầu tư",
    idKey: "id",
    headers: ["Mã", "Giá | % thay đổi", "Lãi/lỗ", "Báo cáo mới"],
    PriceCell: PortfolioPriceCell,
    getSecondaryChange: (item) => item.pnl_pct ?? item.change_pct,
  },
  watchlist: {
    menuHeading: "Danh sách quan sát",
    idKey: "guid",
    headers: ["Mã", "Giá hiện tại", "% thay đổi", "Báo cáo mới"],
    PriceCell: SimplePriceCell,
    getSecondaryChange: (item) => item.change_pct,
  },
};

function WatchlistEmptyState() {
  return (
    <div className="mt-[24px] flex flex-col items-center text-center">
      <EmptyPortfolioIllustration />
      <p className="mt-[16px] text-[16px] font-medium text-[#111827] leading-[24px] m-0">
        Danh mục hiện đang trống
      </p>
      <p className="mt-[4px] text-[14px] font-normal text-[#636369] leading-[20px] m-0">
        Thêm tài sản để Nebula ưu tiên báo cáo cho mã bạn đang nắm giữ.
      </p>
      <button
        type="button"
        onClick={() =>
          window.open(`${BASE_URL}/portfolio`, "_blank", "noopener,noreferrer")
        }
        className="mt-[16px] h-[48px] w-[198px] bg-[#3c3c43] text-white text-[16px] font-medium rounded-[12px] shrink-0"
      >
        Tạo danh mục ngay
      </button>
    </div>
  );
}

function SectionDropdown({ title, list, activeId, idKey, onSelect, menuHeading }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target))
        setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  if (!list || list.length < 2) {
    return (
      <p className="text-[12px] font-bold text-[#636369] leading-[18px] m-0">
        {title}
      </p>
    );
  }

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        className="flex flex-row items-center gap-[4px] cursor-pointer"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="text-[12px] font-bold text-[#636369] leading-[18px]">
          {title}
        </span>
        <ChevronDownIcon
          className={`transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="absolute left-0 top-[calc(100%+8px)] z-10 w-[200px] bg-white rounded-[16px] shadow-[0_8px_24px_rgba(0,0,0,0.12)] border border-[#ececed] py-[12px]">
          {menuHeading ? (
            <p className="px-[16px] text-[12px] font-medium text-[#636369] leading-[18px] mb-[8px]">
              {menuHeading}
            </p>
          ) : null}
          <div className="thin-scrollbar max-h-[118px] overflow-y-auto">
            {list.map((item) => {
              const isActive = item[idKey] === activeId;
              return (
                <button
                  key={item[idKey]}
                  type="button"
                  className="w-full flex flex-row items-center justify-between gap-[12px] px-[16px] py-[4px] text-left hover:bg-[#f9f9fa]"
                  onClick={() => {
                    onSelect?.(item[idKey]);
                    setOpen(false);
                  }}
                >
                  <span
                    className="text-[14px] font-[400] text-[#111827] leading-[20px] truncate"
                    title={item.name}
                  >
                    {item.name}
                  </span>
                  {isActive && (
                    <span className="min-w-[16px] min-h-[16px] shrink-0 mr-[-16px]">
                      <CheckIcon />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function DataRow({ item, config, isSelected, onPick }) {
  const { PriceCell, getSecondaryChange } = config;
  const secondary = formatChange(getSecondaryChange(item));
  return (
    <div
      className={`${GRID} h-[38px] shrink-0 items-center cursor-pointer rounded-[8px] px-[10px] transition-colors ${isSelected ? "bg-[#f5f5f6]" : "hover:bg-[#f9f9fa]"}`}
      onClick={() => onPick?.(item)}
    >
      <span className="text-[14px] font-bold text-[#111827] leading-[20px] truncate">
        {item.symbol}
      </span>
      <PriceCell item={item} />
      <span
        className="text-[14px] font-medium leading-[20px] text-right truncate"
        style={{ color: secondary.color }}
      >
        {secondary.text}
      </span>
      <span className="text-[14px] font-medium text-[#111827] leading-[20px] text-right truncate">
        {reportCountLabel(item.report_count)}
      </span>
    </div>
  );
}

function DataSection({
  type,
  title,
  action,
  data,
  onPick,
  selectedSymbols,
  list,
  activeId,
  onSwitch,
  className = "",
}) {
  const config = SECTION_CONFIGS[type];
  return (
    <section className={`flex flex-col px-[24px] pt-[16px] ${className}`}>
      <div className="flex flex-row justify-between items-center">
        <SectionDropdown
          title={title}
          list={list}
          activeId={activeId}
          idKey={config.idKey}
          onSelect={onSwitch}
          menuHeading={config.menuHeading}
        />
        {action ? (
          <a
            href={action.href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[12px] font-medium text-[#636369] leading-[18px] m-0"
          >
            {action.label}
          </a>
        ) : null}
      </div>
      <div className="thin-scrollbar mt-[12px] flex flex-col flex-1 min-h-0 -mr-[24px] overflow-y-auto overflow-x-hidden">
        <div className={`${GRID} sticky top-0 z-[1] bg-white px-[10px] pr-[34px] pb-[8px]`}>
          {config.headers.map((label, i) => (
            <span
              key={label}
              className={`text-[10px] font-bold text-[#a0a0a5] leading-[16px] ${i === 0 ? "" : "text-right"}`}
            >
              {label}
            </span>
          ))}
        </div>
        <div className="flex flex-col gap-[8px] pr-[24px] flex-1 min-h-0">
          {!data?.length ? (
            <div className="flex flex-col items-center justify-center flex-1 text-center p-[24px]">
              <EmptyDataStateIcon />
              <p className="mt-[12px] text-[14px] text-[#636369]">
                Chưa có dữ liệu để hiển thị
              </p>
            </div>
          ) : (
            data.map((item) => (
              <DataRow
                key={item.canonical_id || item.symbol}
                item={item}
                config={config}
                isSelected={selectedSymbols?.has(item.symbol)}
                onPick={onPick}
              />
            ))
          )}
        </div>
      </div>
    </section>
  );
}

function buildAction(basePath, id, extraPath = "") {
  return id
    ? { href: `${BASE_URL}/${basePath}/${id}${extraPath}`, label: "Xem chi tiết →" }
    : null;
}

export default function InsightsSidebar({
  watchlistData,
  portfolioData,
  selectedSymbols = [],
  onPick,
}) {
  const hasPortfolio = portfolioData && !portfolioData.notFound;
  const selectedSet = new Set(selectedSymbols);
  const handlePick = (h) => onPick?.(h);

  const watchlistSection = (
    <DataSection
      type="watchlist"
      title="ĐANG THEO DÕI"
      action={buildAction("watchlist", watchlistData?.id)}
      data={watchlistData?.items}
      onPick={handlePick}
      selectedSymbols={selectedSet}
      list={watchlistData?.list}
      activeId={watchlistData?.id}
      onSwitch={watchlistData?.switchWatchlist}
      className={hasPortfolio ? "flex-[60] min-h-0" : "flex-1 min-h-0"}
    />
  );

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="border-t border-[#ececed]" />

      {hasPortfolio ? (
        <>
          <DataSection
            type="portfolio"
            title="DANH MỤC"
            action={buildAction("portfolio", portfolioData.id, "/tong-quan")}
            data={portfolioData.items}
            onPick={handlePick}
            selectedSymbols={selectedSet}
            list={portfolioData.list}
            activeId={portfolioData.id}
            onSwitch={portfolioData.switchPortfolio}
            className="flex-[40] min-h-0"
          />
          <div className="mx-[24px] border-t border-[#ececed] mt-[24px]" />
        </>
      ) : (
        <>
          <section className="px-[24px] pt-[20px]">
            <p className="text-[12px] font-bold text-[#636369] leading-[18px] m-0">
              DANH MỤC
            </p>
            <WatchlistEmptyState />
          </section>
          <div className="mt-[40px] mx-[24px] border-t border-[#ececed]" />
        </>
      )}

      {watchlistSection}
    </div>
  );
}
